import { computed, Ref } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import Job, { JobStatus } from '@/models/job'
import { useCurrentUser } from '@/common/useCurrentUser'
import { useLocationOwner } from '@/composables/useLocationOwner'
import { StopConfirmationRequirement } from '@/models/activity'
import { allOf, anyOf, check, usePermission } from '@/components/common/permission'
import { disabledActionMessages as message } from './disabledActionMessages'

export function useJobStopsAndReceiptsPermissions(
  jobNumber: Ref<number>,
  locationOwnerId: Ref<string | null>,
) {
  const store = useStore()
  const currentUserContext = useCurrentUser()
  const { isLocationOwner } = useLocationOwner(locationOwnerId)

  const job = computed<Job | null>(() => store.getters['jobs/getById'](jobNumber.value) ?? null)
  const jobInfo = computed(() => ({
    status: job.value?.status,
    isApproved: job.value?.isApproved,
    serviceProvider: job.value?.serviceProviderBusinessUnitId,
  }))

  const confirmationRequirement = computed<StopConfirmationRequirement>(() =>
    job.value
      ? store.getters['activities/getById'](job.value.activityId)?.stopConfirmationRequirement
      : undefined,
  )

  const amIInTheAssignedSp = computed(() => {
    return (currentUserContext.value?.lineage ?? [])
      .map((l) => l.id)
      .includes(jobInfo.value.serviceProvider ?? '')
  })

  const isDispatcherOrAdmin = computed<boolean>(() => {
    return store.getters['permissions/isCoordinator'] || store.getters['permissions/isAdmin']
  })

  const validStatuses = [JobStatus.ACTIVE, JobStatus.PAUSED, JobStatus.COMPLETED]
  const { permission: jobActionPermission, reasons: jobActionDisabledReasons } = usePermission(() =>
    allOf(
      check(!!job.value && !!currentUserContext.value, message.context),
      check(
        confirmationRequirement.value !== StopConfirmationRequirement.None,
        message.confirmationsDisabled,
      ),
      check(!job.value || validStatuses.includes(job.value.status), message.executionStatus),
    ),
  )

  const { permission: dispatcherOrAdminPermission, reasons: receiptRoleDisabledReasons } =
    usePermission(() => check(isDispatcherOrAdmin.value, message.role))
  const assignedProviderPermission = computed(() =>
    check(amIInTheAssignedSp.value, message.assignedProvider),
  )
  const unapprovedJobPermission = computed(() => check(!jobInfo.value.isApproved, message.approved))
  const locationOwnerPermission = computed(() =>
    check(isLocationOwner.value, message.locationOwner),
  )

  const {
    permission: createStopsPermission,
    allowed: canCreateStops,
    reasons: createStopsDisabledReasons,
  } = usePermission(() =>
    allOf(
      jobActionPermission.value,
      dispatcherOrAdminPermission.value,
      assignedProviderPermission.value,
      unapprovedJobPermission.value,
    ),
  )

  // Location owners may change receipts even after approval. Assigned providers may
  // change them only before approval.
  const {
    permission: overwriteReceiptsPermission,
    allowed: canOverwriteInventoryReceipts,
    reasons: overwriteReceiptsDisabledReasons,
  } = usePermission(() =>
    allOf(
      jobActionPermission.value,
      dispatcherOrAdminPermission.value,
      anyOf(
        locationOwnerPermission.value,
        allOf(assignedProviderPermission.value, unapprovedJobPermission.value),
      ),
    ),
  )

  const isJobCompleted = computed(() => jobInfo.value.status === JobStatus.COMPLETED)

  return {
    canOverwriteInventoryReceipts,
    canCreateStops,
    isJobCompleted,
    jobActionPermission,
    dispatcherOrAdminPermission,
    createStopsPermission,
    overwriteReceiptsPermission,
    jobActionDisabledReasons,
    receiptRoleDisabledReasons,
    createStopsDisabledReasons,
    overwriteReceiptsDisabledReasons,
  }
}
